import { useState } from 'react'
import { getObjects, getObjectsWithFilter } from '../../api/apiClientCrud'
import dayjs from 'dayjs'

export const useFieldValidation = (field, data) => {
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  const validateField = async () => {
    const value = data[field.name]
    let errorMessage = null

    let validations = field.validation ?? []
    if (field.type === 'float' && !validations.some((v) => v.rule === 'float')) {
      validations = [...validations, { rule: 'float', error: `${field.label} doit être un nombre décimal valide` }]
    }
    if ((field.type === 'number' || field.type === 'integer') && !validations.some((v) => v.rule === 'numbers')) {
      validations = [...validations, { rule: 'numbers', error: `${field.label} doit être un nombre entier valide` }]
    }

    if (validations && !field.validationWhen) {
      for (const validation of validations) {
        const rule = validation.rule

        const isTrulyEmpty = value === null || value === undefined || value === ''
        const isNumericType = ['number', 'integer', 'float'].includes(field.type)

        if (rule !== 'req' && isTrulyEmpty && !isNumericType) {
          continue
        }

        if (rule === 'req' && (value === null || value === undefined || value === '' || value.length === 0)) {
          errorMessage = validation.error
          break
        }

        if (rule === 'email' && value && !/^\S+@\S+\.\S+$/.test(value)) {
          errorMessage = validation.error
          break
        }

        if (rule.startsWith('minlen=')) {
          const min = parseInt(rule.split('=')[1])
          if (value?.length < min) {
            errorMessage = validation.error
            break
          }
        }

        if (rule.startsWith('maxlen=')) {
          const max = parseInt(rule.split('=')[1])
          if (value?.length > max) {
            errorMessage = validation.error
            break
          }
        }

        if (rule.startsWith('len=')) {
          const len = parseInt(rule.split('=')[1])
          if ((value?.length ?? 0) !== len) {
            errorMessage = validation.error
            break
          }
        }

        if (rule.startsWith('same=')) {
          const otherField = rule.split('=')[1]
          if (value !== data[otherField]) {
            errorMessage = validation.error
            break
          }
        }

        if (rule.startsWith('after=')) {
          const otherField = rule.split('=')[1]
          if (!dayjs(value).isAfter(dayjs(data[otherField]))) {
            errorMessage = validation.error
            break
          }
        }

        if (rule.startsWith('diff=')) {
          const otherField = rule.split('=')[1]
          if (value === data[otherField]) {
            errorMessage = validation.error
            break
          }
        }

        if (rule.startsWith('regex=')) {
          const regex = new RegExp(rule.split('=')[1])
          if (!regex.test(value)) {
            errorMessage = validation.error
            break
          }
        }

        const isNumberType = field.type === 'number' || field.type === 'integer' || rule === 'numbers'
        const isFloatType = field.type === 'float' || rule === 'float' || rule === 'currency'

        if (value !== undefined && value !== null && value !== '') {
          const normalizedValue = String(value).replace(',', '.')

          if (isNumberType && !/^\d+$/.test(normalizedValue)) {
            errorMessage = validation.error || `${field.label} doit être un nombre entier`
            break
          }

          if (isFloatType && !/^\d+(\.\d+)?$/.test(normalizedValue)) {
            errorMessage = validation.error || `${field.label} doit être un nombre décimal`
            break
          }
        }

        if (rule === 'unique') {
          try {
            const res = await getObjects(validation.object, validation.condition(data))
            if (Array.isArray(res) && res.length > 0) {
              errorMessage = validation.error
              break
            }
          } catch (err) {
            if (validation.except && validation.except(err, data)) {
              errorMessage = validation.error
              break
            }
          }
        }

        if (rule === 'classification') {
          try {
            const res = await getObjectsWithFilter(
              'classification',
              validation.filter(data),
              validation.subfilter?.(data)
            )
            const total = res?.data?.totalItems ?? 0
            const classifMax = res?.data?.classification?.max ?? 1000
            if (total >= classifMax) {
              errorMessage = validation.error
              break
            }
          } catch (err) {
            if (!(validation.except && validation.except(err, data))) {
              errorMessage = validation.error
              break
            }
          }
        }
      }
    }

    setHasError(!!errorMessage)
    setErrorMessage(errorMessage)
    return errorMessage
  }

  return {
    hasError,
    errorMessage,
    validateField
  }
}
